import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactApexChart from 'react-apexcharts';

import jsPDF from 'jspdf';
import { CSVLink } from 'react-csv';
import { 
  FileSpreadsheet, 
  File, 
  Table, 
  BarChart2, 
  PieChart,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
const ChartView = () => {
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = () => {
    setIsLoading(true);
    setTimeout(() => {
      const mockData = [
        { time: '05:00 - 06:00', rawBioGas: 0, cbgCascades: 0 },
        { time: '06:00 - 07:00', rawBioGas: 50.5, cbgCascades: 177.60 },
        { time: '07:00 - 08:00', rawBioGas: 75.2, cbgCascades: 175.48 },
        { time: '08:00 - 09:00', rawBioGas: 120.3, cbgCascades: 220.15 },
        { time: '09:00 - 10:00', rawBioGas: 180.6, cbgCascades: 265.72 },
        { time: '10:00 - 11:00', rawBioGas: 210.4, cbgCascades: 300.55 }
      ];
      
      setHistoryData(mockData);
      setIsLoading(false);
    }, 500);
  };

  // Chart configuration for Raw Bio-Gas
  const rawBioGasChartOptions = {
    series: [{
      name: 'Raw Bio-Gas',
      data: historyData.map(item => item.rawBioGas)
    }],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: historyData.map(item => item.time.split(' - ')[0]),
        title: {
          text: 'Time (Hourly)'
        }
      },
      yaxis: {
        title: {
          text: 'Raw Bio-Gas (m³/hr)'
        }
      },
      title: {
        text: 'Hourly Raw Bio-Gas Production',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      fill: {
        colors: ['#3B82F6']
      },
      tooltip: {
        theme: 'light'
      }
    }
  };

  // Chart configuration for CBG Cascades
  const cbgCascadesChartOptions = {
    series: [{
      name: 'CBG Cascades',
      data: historyData.map(item => item.cbgCascades)
    }],
    options: {
      chart: {
        type: 'area',
        height: 350,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth'
      },
      xaxis: {
        categories: historyData.map(item => item.time.split(' - ')[0]),
        title: {
          text: 'Time (Hourly)'
        }
      },
      yaxis: {
        title: {
          text: 'CBG Cascades (kg/hr)'
        }
      },
      title: {
        text: 'Hourly CBG Filled to Cascades',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
      tooltip: {
        theme: 'light'
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 h-96 overflow-y-auto"
    >
      {isLoading ? (
        <div className="h-full flex items-center justify-center">
          <p className="text-gray-500">Loading chart data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80">
            <ReactApexChart
              options={rawBioGasChartOptions.options}
              series={rawBioGasChartOptions.series}
              type="bar"
              height="100%"
            />
          </div>
          
          <div className="h-80">
            <ReactApexChart
              options={cbgCascadesChartOptions.options}
              series={cbgCascadesChartOptions.series}
              type="area"
              height="100%"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Summary Component
const SummaryView = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching summary data
    setIsLoading(true);
    setTimeout(() => {
      const data = {
        totalRawBioGas: 637.0,
        totalCbgCascades: 1139.5,
        efficiency: 87.5,
        peakHour: '10:00 - 11:00',
        trend: 'increasing'
      };
      setSummaryData(data);
      setIsLoading(false);
    }, 700);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6  overflow-y-auto"
    >
      {isLoading ? (
        <div className="h-full flex items-center justify-center">
          <p className="text-gray-500">Loading summary data...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-gray-800">Production Summary</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500">Total Raw Bio-Gas</h3>
              <p className="text-2xl font-bold text-blue-600">{summaryData.totalRawBioGas} m³</p>
              <p className="text-sm text-gray-600 mt-1">Daily production volume</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500">Total CBG Cascades</h3>
              <p className="text-2xl font-bold text-green-600">{summaryData.totalCbgCascades} kg</p>
              <p className="text-sm text-gray-600 mt-1">Total compressed output</p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">Production Analysis</h3>
            <ul className="mt-2 space-y-2">
              <li className="flex items-center">
                <span className="w-32 text-gray-600">Efficiency:</span>
                <span className="font-medium">{summaryData.efficiency}%</span>
              </li>
              <li className="flex items-center">
                <span className="w-32 text-gray-600">Peak Hour:</span>
                <span className="font-medium">{summaryData.peakHour}</span>
              </li>
              <li className="flex items-center">
                <span className="w-32 text-gray-600">Trend:</span>
                <span className="font-medium capitalize">{summaryData.trend}</span>
              </li>
            </ul>
          </div>
          
          <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
            <h3 className="text-sm font-medium text-amber-700">Insights</h3>
            <p className="mt-1 text-gray-600">
              Production efficiency has been consistently above target with a steady increase throughout the day. 
              The peak production hour generated 210.4 m³ of raw biogas, which was converted to 300.55 kg of CBG.
              Continue monitoring system performance to maintain optimal conversion rates.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};
// Filter data based on date range
 
const ReportSection = ({ onBackToDashboard }) => {
  const [activeView, setActiveView] = useState('table');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [analyticsData, setAnalyticsData] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
const filteredData = React.useMemo(() => {
  if (!dateRange.from && !dateRange.to) return analyticsData;
  
  return analyticsData.filter(item => {
    const itemDate = new Date(item.date);
    
    if (dateRange.from && dateRange.to) {
      return itemDate >= dateRange.from && itemDate <= dateRange.to;
    } else if (dateRange.from) {
      return itemDate >= dateRange.from;
    } else if (dateRange.to) {
      return itemDate <= dateRange.to;
    }
    
    return true;
  });
}, [analyticsData, dateRange]);
  
   // CSV data and headers for download
   const csvData = React.useMemo(() => {
    return [
      ['Date', 'Value', 'Status', 'Battery Level'],
      ...filteredData.map(item => [
        item.date,
        item.value,
        item.status,
        `${item.batteryLevel}%`
      ])
    ];
  }, [filteredData]);

  // Sample devices
  const devices = [
    { id: '1', name: 'Biogas Reactor A' },
    { id: '2', name: 'Biogas Reactor B' },
    { id: '3', name: 'Waste Processing Unit' },
    { id: '4', name: 'Energy Storage System' }
  ];

  // Sample report data
  const reportData = [
    { 
      id: 1, 
      device: 'Biogas Reactor A', 
      date: '2024-03-15', 
      production: '450 m³', 
      efficiency: '85%', 
      carbonOffset: '320 kg' 
    },
    { 
      id: 2, 
      device: 'Biogas Reactor A', 
      date: '2024-03-16', 
      production: '475 m³', 
      efficiency: '88%', 
      carbonOffset: '340 kg' 
    },
  ];

  const handleDownloadCSV = () => {
    const csvContent = [
      'Device,Date,Production,Efficiency,Carbon Offset',
      ...reportData.map(row => 
        `${row.device},${row.date},${row.production},${row.efficiency},${row.carbonOffset}`
      ).join('\n')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'biogas_report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

 // Handle download PDF
 const handleDownloadPDF = () => {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.text("Device Analytics Report", 20, 20);
  
  // Add device info
  doc.setFontSize(12);
  const selectedDeviceName = devices.find(d => d.id === selectedDevice)?.name || 'All Devices';
  doc.text(`Device: ${selectedDeviceName}`, 20, 30);
  
  // Add date range info
  const dateInfo = `Date Range: ${dateRange.from ? format(dateRange.from, 'MMM dd, yyyy') : 'Start'} to ${dateRange.to ? format(dateRange.to, 'MMM dd, yyyy') : 'Present'}`;
  doc.text(dateInfo, 20, 40);
  
  // Add table headers
  const headers = ['Date', 'Value', 'Status', 'Battery Level'];
  let y = 60;
  
  doc.setFontSize(10);
  doc.text(headers[0], 20, y);
  doc.text(headers[1], 70, y);
  doc.text(headers[2], 120, y);
  doc.text(headers[3], 170, y);
  
  y += 10;
  
  // Add table data
  filteredData.forEach((item, index) => {
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
    
    doc.text(item.date, 20, y);
    doc.text(item.value.toString(), 70, y);
    doc.text(item.status, 120, y);
    doc.text(`${item.batteryLevel}%`, 170, y);
    
    y += 10;
  });
  
  // Save the PDF
  doc.save(`device-report-${new Date().getTime()}.pdf`);
};

  const renderViewSwitch = () => {
    const viewOptions = [
      { 
        id: 'table', 
        icon: <Table size={18} />, 
        label: 'Table View' 
      },
      { 
        id: 'chart', 
        icon: <BarChart2 size={18} />, 
        label: 'Chart View' 
      },
      { 
        id: 'summary', 
        icon: <PieChart size={18} />, 
        label: 'Summary View' 
      }
    ];

    return (
      <div className="flex items-center space-x-2 bg-gray-100 rounded-full p-1">
        {viewOptions.map((option) => (
          <motion.button
            key={option.id}
            onClick={() => setActiveView(option.id)}
            className={`
              flex items-center space-x-2 px-4 py-2 rounded-full transition-all
              ${activeView === option.id 
                ? 'bg-teal-600 text-white'
                : 'text-gray-700 hover:bg-gray-200'
              }
            `}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {option.icon}
            <span className="text-sm">{option.label}</span>
          </motion.button>
        ))}
      </div>
    );
  };

  const renderActiveView = () => {
    switch(activeView) {
      case 'table':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-700 uppercase">
                <tr>
                  <th className="px-4 py-3">Device</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Production</th>
                  <th className="px-4 py-3">Efficiency</th>
                  <th className="px-4 py-3">Carbon Offset</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <motion.tr 
                    key={row.id} 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ 
                      opacity: 1, 
                      x: 0,
                      transition: { 
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 300,
                        damping: 20
                      }
                    }}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3">{row.device}</td>
                    <td className="px-4 py-3">{row.date}</td>
                    <td className="px-4 py-3">{row.production}</td>
                    <td className="px-4 py-3">{row.efficiency}</td>
                    <td className="px-4 py-3">{row.carbonOffset}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        );
      case 'chart':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className=""
          >
            <ChartView />
          </motion.div>
        );
      case 'summary':
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className=""
          >
             <SummaryView />
          </motion.div>
        );
      default:
        return null;
    }
  };
  return (
    <div className='min-h-screen p-2 md:p-4 lg:p-6'>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-4 md:space-y-6"
      >
        {/* Page Header */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <motion.div 
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-3"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">
              Reports & Analytics
            </h2>
          </motion.div>
  
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-wrap gap-2 sm:gap-3"
          >
            <CSVLink 
              data={csvData} 
              filename={`device-report-${new Date().getTime()}.csv`}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 text-sm md:px-4 md:py-2 md:text-base rounded-md flex items-center space-x-2"
            >
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadCSV}
                className="flex items-center space-x-1 md:space-x-2"
              >
                <FileSpreadsheet size={14} className="md:w-4 md:h-4" />
                <span>Download CSV</span>
              </motion.button>
            </CSVLink>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDownloadPDF}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-sm md:px-4 md:py-2 md:text-base rounded-md flex items-center space-x-1 md:space-x-2"
            >
              <File size={14} className="md:w-4 md:h-4" />
              <span>Download PDF</span>
            </motion.button>
          </motion.div>
        </div>
  
        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6"
        >
          {/* Device Selection */}
          <div className="bg-white rounded-lg shadow-md p-3 md:p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
              Select Device
            </label>
            <select 
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="w-full px-2 py-1 md:px-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
            >
              <option value="">Choose Device</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.name}
                </option>
              ))}
            </select>
          </div>
  
          {/* Date Range Picker */}
          <div className="bg-white rounded-lg shadow-md p-3 md:p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
              Date Range
            </label>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <input 
                type="date" 
                value={dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: new Date(e.target.value) }))}
                className="w-full px-2 py-1 md:px-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
              />
              <input 
                type="date" 
                value={dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: new Date(e.target.value) }))}
                className="w-full px-2 py-1 md:px-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm md:text-base"
              />
            </div>
          </div>
  
          {/* View Switch */}
          <div className="bg-white rounded-lg shadow-md p-3 md:p-4 flex items-center justify-center">
            {renderViewSwitch()}
          </div>
        </motion.div>
  
        {/* Dynamic Content Area */}
        <div className="w-full overflow-x-auto">
          {renderActiveView()}
        </div>
      </motion.div>
    </div>
  );
};

export default ReportSection;
